import React from "react";
import { motion } from "framer-motion";
import {
  Rocket,
  Lightbulb,
  BarChart4,
  LineChart,
  PieChart,
} from "lucide-react";

function MissionAndVision() {
  return (
    <section
      className="bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] py-16 md:py-24 relative overflow-hidden"
      id="about-us"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-[#e2e8f0]/40"
            style={{
              width: `${Math.random() * 100 + 30}px`,
              height: `${Math.random() * 100 + 30}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="flex flex-col items-center text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="relative inline-block mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
              Our Mission & Vision
            </h2>
            <motion.div
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-red-500"
              initial={{ width: 0 }}
              whileInView={{ width: "4rem" }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </motion.div>
          <motion.p
            className="text-base md:text-lg text-gray-600 max-w-2xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Guiding startups to financial excellence and sustainable growth
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Metrics Card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Growth Trajectory */}
                <div className="col-span-2 bg-red-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center mb-3">
                    <BarChart4 className="text-red-500 w-5 h-5 mr-2" />
                    <span className="text-sm text-gray-700">
                      Growth Trajectory
                    </span>
                  </div>
                  <div className="flex items-end space-x-1 h-16">
                    {[25, 35, 50, 65, 100, 85].map((height, i) => (
                      <motion.div
                        key={i}
                        className="w-3 bg-gradient-to-t from-red-500 to-red-300 rounded-t"
                        style={{ height: `${height}%` }}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${height}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1, duration: 0.7 }}
                      />
                    ))}
                  </div>
                </div>

                {/* ROI Metric */}
                <div className="bg-red-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center">
                  <div className="flex items-center mb-2">
                    <PieChart className="text-red-500 w-4 h-4 mr-1" />
                    <span className="text-xs text-gray-700">ROI</span>
                  </div>
                  <motion.div
                    className="text-lg font-bold text-red-600"
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    3.8x
                  </motion.div>
                </div>

                {/* Efficiency Metric */}
                <div className="bg-red-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center">
                  <div className="flex items-center mb-2">
                    <LineChart className="text-red-500 w-4 h-4 mr-1" />
                    <span className="text-xs text-gray-700">Efficiency</span>
                  </div>
                  <motion.div
                    className="relative w-8 h-8 rounded-full border-2 border-red-500 flex items-center justify-center"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <span className="text-[10px] text-red-600">85%</span>
                  </motion.div>
                </div>
              </div>
            </div>
            <motion.div
              className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-red-500"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.7 }}
            />
            <motion.div
              className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-red-500"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.8 }}
            />
          </motion.div>

          <div className="space-y-6">
            {/* Mission Card */}
            <motion.div
              className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 transition-all duration-300 hover:border-red-500 group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
              whileHover={{ y: -10 }}
            >
              <div className="flex items-start">
                <motion.div
                  className="bg-red-100 p-2 rounded-lg mr-3 group-hover:bg-red-500 transition-colors"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  <Rocket
                    className="text-red-500 w-6 h-6 group-hover:text-white transition-colors"
                    strokeWidth={1.5}
                  />
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Our Mission
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Enabling successful growth for driven startups with the
                    financial resources and expertise they require.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Vision Card */}
            <motion.div
              className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 transition-all duration-300 hover:border-red-500 group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.5 }}
              whileHover={{ y: -10 }}
            >
              <div className="flex items-start">
                <motion.div
                  className="bg-red-100 p-2 rounded-lg mr-3 group-hover:bg-red-500 transition-colors"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  <Lightbulb
                    className="text-red-500 w-6 h-6 group-hover:text-white transition-colors"
                    strokeWidth={1.5}
                  />
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Our Vision
                  </h3>
                  <p className="text-gray-600 text-sm">
                    To be the preeminent virtual finance team for startups,
                    guiding them to reach their full potential through creative
                    financial solutions.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-red-100 blur-xl"
        animate={{
          y: [0, -15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-red-100 blur-xl"
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />
    </section>
  );
}

export default MissionAndVision;
